import React, { useState, useEffect } from "react";
import { ArrowLeft, Trash2, UserCog, Shield, Check, X, Search, Edit2, Ban, User } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  
  // Edit Form State
  const [editForm, setEditForm] = useState({
    first_name: "",
    last_name: "",
    role: "",
    is_restricted: false
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setUsers(response.data);
    } catch (error) {
      toast.error("Ошибка загрузки пользователей");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Вы уверены, что хотите удалить этого пользователя? Это действие необратимо.")) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success("Пользователь удален");
      setUsers(users.filter(u => u.user_id !== userId));
      setShowEditDialog(false);
    } catch (error) {
      toast.error("Ошибка удаления пользователя");
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;

    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/users/${selectedUser.user_id}`, {
        first_name: editForm.first_name,
        last_name: editForm.last_name,
        role: editForm.role,
        is_restricted: editForm.is_restricted
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      
      toast.success("Пользователь обновлен");
      
      setUsers(users.map(u => 
        u.user_id === selectedUser.user_id 
          ? { 
              ...u, 
              first_name: editForm.first_name, 
              last_name: editForm.last_name, 
              role: editForm.role, 
              is_admin: editForm.role === "coach",
              is_restricted: editForm.is_restricted
            } 
          : u
      ));
      
      setShowEditDialog(false);
    } catch (error) {
      toast.error("Ошибка обновления");
    }
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setEditForm({
      first_name: user.first_name,
      last_name: user.last_name,
      role: user.role,
      is_restricted: user.is_restricted || false
    });
    setShowEditDialog(true);
  };

  const filteredUsers = users.filter(u => 
    u.first_name?.toLowerCase().includes(search.toLowerCase()) || 
    u.last_name?.toLowerCase().includes(search.toLowerCase()) || 
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  const getRoleLabel = (role) => {
    switch (role) {
      case "coach": return "Тренер";
      case "athlete": return "Спортсмен";
      case "parent": return "Родитель";
      case "amateur": return "Любитель";
      default: return role;
    }
  };

  return (
    <div className="min-h-screen bg-white p-4">
      <div className="flex items-center gap-4 mb-6">
        <Button onClick={() => navigate("/home")} variant="ghost" size="icon">
          <ArrowLeft className="w-6 h-6" />
        </Button>
        <h1 className="font-heading text-2xl font-bold">Управление</h1>
      </div>

      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
        <Input 
          className="pl-10 h-12 bg-apple-gray border-transparent rounded-xl" 
          placeholder="Поиск пользователей..." 
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-golf-green border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredUsers.map(user => (
            <div 
              key={user.user_id} 
              onClick={() => openEditDialog(user)}
              className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-golf-green/10 flex items-center justify-center text-golf-green font-semibold text-lg relative">
                  {user.first_name?.[0]}{user.last_name?.[0]}
                  {user.is_restricted && (
                    <div className="absolute -bottom-1 -right-1 bg-red-500 rounded-full p-0.5 border-2 border-white">
                      <Ban className="w-3 h-3 text-white" />
                    </div>
                  )}
                </div>
                <div>
                  <p className="font-semibold text-base">{user.first_name} {user.last_name}</p>
                  <p className="text-sm text-apple-text-secondary">{user.email}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`text-xs px-2 py-0.5 rounded-md ${
                      user.role === 'coach' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {getRoleLabel(user.role)}
                    </span>
                    {user.is_master && (
                      <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-md flex items-center gap-1">
                        <Shield className="w-3 h-3" /> Master
                      </span>
                    )}
                  </div>
                </div>
              </div>
              
              <UserCog className="w-5 h-5 text-gray-300" />
            </div>
          ))}
        </div>
      )}

      {/* Edit User Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Редактирование пользователя</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-5">
            
            <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-xl mb-2">
              <div className="w-14 h-14 rounded-full bg-white border border-gray-200 flex items-center justify-center text-xl font-bold text-golf-green">
                {selectedUser?.first_name?.[0]}{selectedUser?.last_name?.[0]}
              </div>
              <div>
                <p className="font-medium text-gray-500 text-sm">Email</p>
                <p className="text-black font-semibold">{selectedUser?.email}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Имя</Label>
                <Input 
                  value={editForm.first_name}
                  onChange={(e) => setEditForm({...editForm, first_name: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Фамилия</Label>
                <Input 
                  value={editForm.last_name}
                  onChange={(e) => setEditForm({...editForm, last_name: e.target.value})}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Роль</Label>
              <Select value={editForm.role} onValueChange={(val) => setEditForm({...editForm, role: val})}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Выберите роль" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="athlete">Спортсмен</SelectItem>
                  <SelectItem value="coach">Тренер</SelectItem>
                  <SelectItem value="parent">Родитель</SelectItem>
                  <SelectItem value="amateur">Любитель</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between p-4 border border-gray-200 rounded-xl">
              <div className="space-y-0.5">
                <Label className="text-base">Ограничить действия</Label>
                <p className="text-xs text-gray-500">Запретить отправку сообщений и файлов</p>
              </div>
              <Switch
                checked={editForm.is_restricted}
                onCheckedChange={(checked) => setEditForm({...editForm, is_restricted: checked})}
              />
            </div>

            <div className="pt-2 flex gap-3">
              {!selectedUser?.is_master && (
                <Button 
                  onClick={() => handleDeleteUser(selectedUser?.user_id)} 
                  variant="outline" 
                  className="flex-1 text-red-500 border-red-200 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Удалить
                </Button>
              )}
              <Button onClick={handleUpdateUser} className="flex-1 bg-golf-green hover:bg-golf-green-hover">
                Сохранить
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
